import type { ErrorReportInput, ErrorSource } from "@/schemas/error-log";

/**
 * Automatic frontend error capture (Settings → Error log).
 *
 * Three sources feed it: React's ErrorBoundary, `window.onerror`, and
 * unhandled promise rejections. Grouping, counting and fingerprinting all
 * happen server-side — this module's only jobs are to catch, to keep the
 * volume sane, and to never make things worse.
 *
 * **Never throws and never surfaces anything to the user.** A reporter that
 * can fail loudly turns one bug into two; every failure here is swallowed. It
 * also must not report its own failures, or a broken endpoint becomes an
 * infinite loop — hence the `sending` guard.
 */

/** Don't send the same error more than once per window, per page load. */
const REPEAT_WINDOW_MS = 60_000;
/**
 * Hard cap on distinct errors reported per page load. A render loop throwing
 * fresh messages (a changing index, say) would otherwise walk past the repeat
 * window and hammer the endpoint; the server rate-limits too, but the client
 * shouldn't be the one making that necessary.
 */
const MAX_DISTINCT_PER_LOAD = 25;

const lastSentAt = new Map<string, number>();
let sending = false;
let installed = false;

/** Local throttle key only — the real fingerprint is the server's. */
function throttleKey(report: ErrorReportInput): string {
  return `${report.source}|${report.routePath ?? ""}|${report.message}`;
}

function shouldSend(report: ErrorReportInput): boolean {
  const key = throttleKey(report);
  const now = Date.now();
  const previous = lastSentAt.get(key);
  if (previous !== undefined && now - previous < REPEAT_WINDOW_MS) return false;
  if (previous === undefined && lastSentAt.size >= MAX_DISTINCT_PER_LOAD) return false;
  lastSentAt.set(key, now);
  return true;
}

/** Trim to the schema's caps so a huge stack is never the reason a report 400s. */
function clamp(value: string | undefined | null, max: number): string | null {
  if (!value) return null;
  return value.length > max ? value.slice(0, max) : value;
}

/**
 * Report one error. Fire-and-forget: callers never await this and never see a
 * failure. `keepalive` lets a report survive the navigation that an error on a
 * route transition often triggers.
 */
export function reportClientError(input: {
  source: ErrorSource;
  error: unknown;
  componentStack?: string | null;
}): void {
  try {
    if (sending) return;

    const error = input.error;
    const message =
      error instanceof Error
        ? error.message || error.name
        : typeof error === "string"
          ? error
          : "Unknown error";
    const report: ErrorReportInput = {
      message: clamp(message, 1000) ?? "Unknown error",
      source: input.source,
      stack: error instanceof Error ? clamp(error.stack, 10000) : null,
      componentStack: clamp(input.componentStack, 10000),
      routePath: clamp(window.location.pathname, 500),
    };

    if (!shouldSend(report)) return;

    sending = true;
    void fetch("/api/error-log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(report),
      keepalive: true,
    })
      .catch(() => {
        // Offline, rate-limited, signed out — all fine. The error the user hit
        // is the problem worth their attention, not this.
      })
      .finally(() => {
        sending = false;
      });
  } catch {
    sending = false;
  }
}

/**
 * Install the global handlers. Called once from the app entry; safe to call
 * again (HMR re-runs the entry module).
 */
export function installErrorReporter(): void {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    // Resource load failures (a 404'd image) also fire "error" but carry no
    // Error object and aren't actionable here.
    if (!event.error && !event.message) return;
    reportClientError({ source: "window_error", error: event.error ?? event.message });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    reportClientError({ source: "unhandled_rejection", error: event.reason });
  });
}
